// ---------------------------------------------------------------------------
// Clock arithmetic.
//
// Every clock is an absolute deadline plus accumulated pause time. Nothing in
// the codebase should ever store or transmit a remaining-milliseconds counter:
// that is what drifts, and what fast-forwards across a restart.
// ---------------------------------------------------------------------------
import { HEARTBEAT_GRACE_MS } from './constants.js';
export function startClock(durationMs, now) {
    return {
        durationMs,
        startedAt: now,
        deadline: now + durationMs,
        accumulatedPauseMs: 0,
        pausedAt: null,
    };
}
/** The deadline once every pause so far is accounted for. */
export function effectiveDeadline(clock) {
    return clock.deadline + clock.accumulatedPauseMs;
}
export function remainingMs(clock, now) {
    const reference = clock.pausedAt ?? now;
    return Math.max(0, effectiveDeadline(clock) - reference);
}
export function isExpired(clock, now) {
    return remainingMs(clock, now) <= 0;
}
/**
 * How much of the clock has actually been spent, pauses excluded.
 *
 * The only correct way to say "locked with four seconds left". Do NOT reach for
 * `lockedAt - round.startedAt` instead: `startedAt` is the SELECTION phase for
 * letters and numbers, so that subtraction silently includes the whole calling
 * budget, and it counts every pause the room took as thinking time. This goes
 * through `remainingMs`, which is the one place the pause accounting lives.
 */
export function elapsedMs(clock, now) {
    return Math.max(0, Math.min(clock.durationMs, clock.durationMs - remainingMs(clock, now)));
}
export function fractionRemaining(clock, now) {
    if (clock.durationMs <= 0)
        return 0;
    return Math.max(0, Math.min(1, remainingMs(clock, now) / clock.durationMs));
}
export function pauseClock(clock, now) {
    if (clock.pausedAt !== null)
        return clock;
    return { ...clock, pausedAt: now };
}
export function resumeClock(clock, now) {
    if (clock.pausedAt === null)
        return clock;
    return {
        ...clock,
        accumulatedPauseMs: clock.accumulatedPauseMs + (now - clock.pausedAt),
        pausedAt: null,
    };
}
// `extendClock` used to live here, and it had exactly one caller: letters
// selection handed the chooser TILE_FLIP_MS back for every tile called. That
// is gone (see `pickLetter`), and with it the only reason to be able to push a
// deadline out without a pause behind it. Do not bring it back to solve a
// "give this player a moment" problem — a clock whose remaining fraction can
// rise is one `PieClock` cannot draw, and the last attempt at drawing it is
// what made the selection timer look broken.
/**
 * A player's own deadline is the earlier of the round deadline and the last
 * moment the server knew they were alive.
 *
 * This is what makes a disconnect nearly costless while closing the
 * pull-the-cable exploit: a player who drops at t=5 can only ever claim to have
 * locked by about t=6.5, whatever their client says afterwards.
 */
export function effectivePlayerDeadline(clock, lastHeartbeat) {
    return Math.min(effectiveDeadline(clock), lastHeartbeat + HEARTBEAT_GRACE_MS);
}
/**
 * Whether a lock that arrived late should still count.
 *
 * Accepts when the client claims it locked before the deadline and that claim is
 * consistent with the last verified heartbeat. Ordinary latency passes: a lock
 * hit at t=29.8 on a 400ms connection arrives after the deadline and is
 * correctly accepted.
 */
export function acceptsLateLock(clock, lastHeartbeat, clientLockedAt) {
    return clientLockedAt <= effectivePlayerDeadline(clock, lastHeartbeat);
}
